// Audit Tax Computation Service
// Corresponds to org.zhuo_yi.audit.controller.AuditTaxComController

import { BASE_AUDIT_API, fetchAPI } from '@/services/api';
import {
    TaxComputationCombinedVO,
    AuditTaxComBO
} from '@/services/auditService/interface/tax-com';

const base = `${BASE_AUDIT_API}/tax-com`;

/*
 * CLOSED with export-taxCom / export-taxReturn (merged into /fs-preview-download/download-zip):
 * FileDownloadResult / fetchFile helpers removed from active path.
 */

/**
 * Query Combined Tax Computation - Main + P1.1/P1.2/P1.21/P1.3 in one call
 *
 * 后端规则：
 * - 主结果 taxComputationResult 始终返回；
 * - 当公司 principalActivities == 0（Inactive）时，子附表 p11/p12/p121/p13 均为 null；
 * - 当对应子附表查询结果不含任何数据时，该字段为 null。
 *
 * @param companyId Company ID
 * @param annualId  Annual ID
 * @param yearOfAssessment Optional YOA slice (empty = legacy/SINGLE)
 */
export async function queryTaxCombined(companyId: number, annualId: number, yearOfAssessment?: string) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    const yoa = (yearOfAssessment ?? '').trim();
    if (yoa) {
        params.set('yearOfAssessment', yoa);
    }
    return fetchAPI<TaxComputationCombinedVO>(`${base}/queryTaxCombined?${params.toString()}`);
}

/**
 * Save or Update Tax Computation Data
 * 
 * @param data AuditTaxComBO request body
 * @returns boolean indicating success
 */
export async function saveOrUpdate(data: AuditTaxComBO) {
    return fetchAPI<boolean>(`${base}/saveOrUpdate`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/**
 * CLOSED: 单文件 Tax Com 下载已合并进 Zip。
 * 请使用 downloadFsPreviewZip（GET /fs-preview-download/download-zip）。
 * GET /tax-com/export-taxCom 后端已注释关闭。
 */
// export async function exportTaxComExcel(companyId: number, annualId: number, yearOfAssessment?: string) {
//     const params = new URLSearchParams({
//         companyId: String(companyId),
//         annualId: String(annualId),
//     });
//     const yoa = (yearOfAssessment ?? '').trim();
//     if (yoa) {
//         params.set('yearOfAssessment', yoa);
//     }
//     return fetchFile(`${base}/export-taxCom?${params.toString()}`, {
//         method: 'GET',
//     });
// }

/**
 * CLOSED: 单文件 Tax Return (PTR) 下载已合并进 Zip。
 * 请使用 downloadFsPreviewZip（GET /fs-preview-download/download-zip）。
 * POST /tax-com/export-taxReturn 后端已注释关闭。
 */
// export async function exportTaxReturnExcel(companyId: number, annualId: number, yearOfAssessment?: string) {
//     const params = new URLSearchParams({
//         companyId: String(companyId),
//         annualId: String(annualId),
//     });
//     const yoa = (yearOfAssessment ?? '').trim();
//     if (yoa) {
//         params.set('yearOfAssessment', yoa);
//     }
//     return fetchFile(`${base}/export-taxReturn?${params.toString()}`, {
//         method: 'POST',
//     });
// }

// Export all functions as a service object
export const auditTaxComService = {
    queryTaxCombined,
    saveOrUpdate,
    // CLOSED → Zip: exportTaxComExcel, exportTaxReturnExcel
};

// Export types from interface file
export type {
    TaxComputationCombinedVO,
    TaxComputationResult,
    TaxDepreciationAllowanceScheduleVO,
    TaxAdditionsNonCurrentAssetsVO,
    TaxObligationUnderFinanceLeasesVO,
    TaxPpeDetailsVO,
    AuditTaxComBO,
    ProfitsTaxAVO,
    ProfitsTaxBVO,
    ProfitsTaxCVO,
    TaxBasicProfitsVO,
    TaxBalancingAllowance,
    TaxCommercialBuilding,
    TaxMotorVehicle,
    TaxMotorHirePurchase,
    TaxNonCurrentAsset,
    TaxPpe,
    TaxCostPerBook,
    AuditTaxComPropertyBO,
} from '@/services/auditService/interface/tax-com';

export { TaxComputationType } from '@/services/auditService/interface/tax-com';
